import { useState, useRef, type KeyboardEvent } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, MoreHorizontal, Pencil, Trash2, ChevronUp, ChevronDown, Percent } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { BucketColorPicker } from './BucketColorPicker'
import { BucketForm } from './BucketForm'
import { DeleteBucketDialog } from './DeleteBucketDialog'
import { useUpdateBucket } from '@/hooks/useBuckets'
import { getBucketIcon } from '@/lib/bucketIcons'
import { cn } from '@/lib/utils'
import type { Bucket } from '@/types'
import type { BucketAlloc } from '@/lib/allocation'

// ─── Target allocation quick-set ─────────────────────────────────────────────

const PRESET_PCTS = [5, 10, 15, 20]

/** Popover body for setting a bucket's target allocation — reusable wherever anchored. */
export function TargetAllocFields({ bucket, onClose }: { bucket: Bucket; onClose?: () => void }) {
  const currentPct = bucket.targetAlloc != null ? Math.round(bucket.targetAlloc * 100) : null
  const [pct, setPct] = useState(currentPct != null ? String(currentPct) : '')
  const updateBucket = useUpdateBucket()

  function applyPct(value: number) {
    updateBucket.mutate({ id: bucket.id, data: { targetAlloc: value / 100 } })
    setPct(String(value))
  }

  function commitInput() {
    const n = parseInt(pct)
    if (!isNaN(n) && n > 0 && n <= 100) {
      updateBucket.mutate({ id: bucket.id, data: { targetAlloc: n / 100 } })
    }
  }

  function clear() {
    updateBucket.mutate({ id: bucket.id, data: { targetAlloc: undefined } })
    setPct('')
    onClose?.()
  }

  const isSet = bucket.targetAlloc != null

  return (
    <>
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
        Target allocation
      </p>
        <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
          What share of your time should {bucket.name} receive?
        </p>

        {/* Preset chips */}
        <div className="flex gap-1.5 mb-3">
          {PRESET_PCTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => applyPct(p)}
              className={cn(
                'flex-1 h-7 rounded text-xs font-medium tabular-nums border transition-colors',
                currentPct === p
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:border-ring hover:text-foreground text-muted-foreground',
              )}
            >
              {p}%
            </button>
          ))}
        </div>

        {/* Free-form input */}
        <div className="flex items-center gap-1.5 mb-2">
          <input
            type="number"
            min="1"
            max="100"
            step="1"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            onBlur={commitInput}
            onKeyDown={(e) => { if (e.key === 'Enter') { commitInput(); onClose?.() } }}
            placeholder="Custom %"
            className="flex-1 h-7 px-2 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring bg-background"
            aria-label="Custom target percentage"
          />
          <span className="text-xs text-muted-foreground flex-shrink-0">%</span>
        </div>

      {isSet && (
        <button
          type="button"
          onClick={clear}
          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
        >
          Clear target
        </button>
      )}
    </>
  )
}

export function TargetAllocPopover({ bucket }: { bucket: Bucket }) {
  const [open, setOpen] = useState(false)
  const currentPct = bucket.targetAlloc != null ? Math.round(bucket.targetAlloc * 100) : null
  const isSet = bucket.targetAlloc != null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'h-9 px-2 md:h-6 md:px-1 rounded text-xs font-medium tabular-nums transition-colors flex items-center',
            isSet ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
          )}
          aria-label={isSet
            ? `Target: ${currentPct}%. Click to change.`
            : 'Set target allocation'}
          title={isSet ? `Target: ${currentPct}%` : 'Set target %'}
        >
          {isSet ? `${currentPct}%` : <Percent size={12} />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-3" align="end" onClick={(e) => e.stopPropagation()}>
        <TargetAllocFields key={open ? 'open' : 'closed'} bucket={bucket} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}

// ─── Allocation bar ────────────────────────────────────────────────────────────

export function AllocationBar({ alloc, color }: { alloc: BucketAlloc; color: string }) {
  const pct = Math.round(alloc.share * 100)
  const targetPct = alloc.targetAlloc != null ? Math.round(alloc.targetAlloc * 100) : null
  const gap = alloc.gap
  const underInvested = gap != null && gap > 0.03

  return (
    <div className="mt-1.5 space-y-0.5">
      <div className="relative h-1 bg-muted/50 rounded-full overflow-visible">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all"
          style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color, opacity: 0.75 }}
        />
        {targetPct != null && (
          <div
            className="absolute top-[-2px] bottom-[-2px] w-0.5 bg-muted-foreground/50 rounded-full"
            style={{ left: `${Math.min(100, targetPct)}%` }}
          />
        )}
      </div>
      <p className={cn(
        'text-[10px] tabular-nums',
        underInvested ? 'text-amber-600' : 'text-muted-foreground',
      )}>
        {pct}%
        {targetPct != null && <span className="opacity-70"> · target {targetPct}%</span>}
      </p>
    </div>
  )
}

interface BucketHeaderProps {
  bucket: Bucket
  bucketAlloc?: BucketAlloc
  onMoveUp?: () => void
  onMoveDown?: () => void
}

export function BucketHeader({ bucket, bucketAlloc, onMoveUp, onMoveDown }: BucketHeaderProps) {
  const isSystem = bucket.isSystem

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: bucket.id,
    disabled: isSystem,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  const [isEditingName, setIsEditingName] = useState(false)
  const [editName, setEditName] = useState('')
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const updateBucket = useUpdateBucket()

  function startNameEdit() {
    setEditName(bucket.name)
    setIsEditingName(true)
    // Input autofocus handles focus
  }

  async function commitNameEdit() {
    const trimmed = editName.trim()
    if (trimmed && trimmed !== bucket.name) {
      await updateBucket.mutateAsync({ id: bucket.id, data: { name: trimmed } })
    }
    setIsEditingName(false)
  }

  function handleNameKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); void commitNameEdit() }
    if (e.key === 'Escape') { setIsEditingName(false) }
  }

  async function handleColorChange(color: string) {
    await updateBucket.mutateAsync({ id: bucket.id, data: { color } })
  }

  const canEdit = !isSystem
  const BucketIconEl = getBucketIcon(bucket.icon)

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="group flex items-start gap-2 py-3"
      >
        {/* Drag handle — hidden until hover, absent for system buckets */}
        <div className="mt-0.5 w-4 flex-shrink-0">
          {canEdit && (
            <button
              {...attributes}
              {...listeners}
              className="max-md:opacity-40 opacity-0 group-hover:opacity-40 hover:!opacity-100 transition-opacity text-muted-foreground cursor-grab active:cursor-grabbing touch-none"
              aria-label={`Drag to reorder ${bucket.name}`}
              tabIndex={0}
            >
              <GripVertical size={16} />
            </button>
          )}
        </div>

        {/* Name + intention */}
        <div className="flex-1 min-w-0">
          {isEditingName ? (
            <input
              ref={nameInputRef}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={() => void commitNameEdit()}
              onKeyDown={handleNameKeyDown}
              autoFocus
              className="w-full bg-transparent font-semibold text-base border-b border-ring focus:outline-none"
              aria-label="Edit bucket name"
            />
          ) : (
            <h2
              className={`flex items-center gap-1.5 font-semibold text-base leading-snug ${canEdit ? 'cursor-text focus-visible:outline-none focus-visible:underline' : 'cursor-default'}`}
              onClick={() => canEdit && startNameEdit()}
              onKeyDown={(e) => canEdit && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), startNameEdit())}
              tabIndex={canEdit ? 0 : undefined}
              role={canEdit ? 'button' : undefined}
              aria-label={canEdit ? `${bucket.name} — press Enter to rename` : undefined}
            >
              {BucketIconEl && (
                <BucketIconEl
                  size={15}
                  style={{ color: bucket.color }}
                  aria-hidden
                  className="flex-shrink-0"
                />
              )}
              {bucket.name}
              {isSystem && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">(system)</span>
              )}
            </h2>
          )}

          {bucket.intention && !isEditingName && (
            <p className="text-sm text-muted-foreground mt-0.5 truncate">{bucket.intention}</p>
          )}

          {/* FR-D5: inline allocation bar (only when cadence data exists or target set) */}
          {bucketAlloc && (bucketAlloc.share > 0 || bucketAlloc.targetAlloc != null) && !isEditingName && (
            <AllocationBar alloc={bucketAlloc} color={bucket.color} />
          )}
        </div>

        {/* Right-side actions — visible on hover or keyboard focus */}
        <div className="flex items-center gap-1 mt-0.5 max-md:opacity-100 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex-shrink-0">
          {canEdit && (
            <TargetAllocPopover bucket={bucket} />
          )}
          {canEdit && (
            <BucketColorPicker color={bucket.color} onChange={handleColorChange} />
          )}

          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 md:h-7 md:w-7 text-muted-foreground"
                  aria-label={`Actions for ${bucket.name}`}
                >
                  <MoreHorizontal size={15} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={startNameEdit}>
                  <Pencil size={14} className="mr-2" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                  <Pencil size={14} className="mr-2" />
                  Edit details
                </DropdownMenuItem>
                {onMoveUp && (
                  <DropdownMenuItem onClick={onMoveUp}>
                    <ChevronUp size={14} className="mr-2" />
                    Move up
                  </DropdownMenuItem>
                )}
                {onMoveDown && (
                  <DropdownMenuItem onClick={onMoveDown}>
                    <ChevronDown size={14} className="mr-2" />
                    Move down
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 size={14} className="mr-2" />
                  Remove area
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <BucketForm
        bucket={bucket}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
      />
      <DeleteBucketDialog
        bucket={showDeleteDialog ? bucket : null}
        onOpenChange={(open) => { if (!open) setShowDeleteDialog(false) }}
      />
    </>
  )
}
